(() => {
  "use strict";

  const state = {
    locale: localStorage.getItem("formOfUsLocale") || "en-US",
    catalog: [],
    dictionary: null,
  };

  const money = (value) => new Intl.NumberFormat(state.locale, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: Number.isInteger(value) ? 0 : 2,
  }).format(value);

  const themeAsset = (name) => window.FORM_OF_US_THEME_ASSETS?.[name] || `/assets/${name}`;
  const getNested = (object, path) => path.split(".").reduce((current, key) => current && current[key], object);
  const copy = (path, fallback) => getNested(state.dictionary, path) || fallback;

  const escapeHtml = (value) => String(value ?? "").replace(/[&<>"']/g, (character) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    "\"": "&quot;",
    "'": "&#39;",
  })[character]);

  function productCard(product) {
    const title = product.title[state.locale] || product.title["en-US"];
    const image = themeAsset(product.image || `product-${product.handle}-v1.png`);
    const productUrl = `/products/${encodeURIComponent(product.handle)}`;
    return `
      <article class="product-card" data-category="${escapeHtml(product.category)}">
        <a class="product-card__visual" href="${productUrl}" aria-label="${escapeHtml(title)}">
          <span class="sale-badge">SAVE ${money(product.compare_at_price - product.price)}</span>
          <img src="${image}" alt="${escapeHtml(title)}" loading="lazy">
        </a>
        <div class="product-card__body">
          <p class="product-card__category">${escapeHtml(copy(`catalog.category_labels.${product.category}`, product.category))}</p>
          <h3><a href="${productUrl}">${escapeHtml(title)}</a></h3>
          <div class="product-card__footer">
            <span class="price"><strong>${escapeHtml(copy("product.from", "From"))} ${money(product.price)}</strong><del>${money(product.compare_at_price)}</del></span>
            <a class="product-card__button" href="${productUrl}">${escapeHtml(copy("product.customize", "Customize"))}</a>
          </div>
        </div>
      </article>`;
  }

  function renderProducts() {
    const carousel = document.querySelector("[data-product-carousel]");
    const featuredOrder = [
      "custom-funko-figurine-from-photo",
      "custom-pet-figurine-keychain",
      "custom-photo-crystal-globe",
      "custom-photo-building-block-figure",
    ];
    const featured = featuredOrder.map((handle) => state.catalog.find((product) => product.handle === handle)).filter(Boolean);
    if (carousel) carousel.innerHTML = featured.map(productCard).join("");
  }

  async function applyLocale(locale) {
    let dictionary = window.FORM_OF_US_LOCALES?.[locale];
    if (!dictionary) {
      const response = await fetch(`/locales/${encodeURIComponent(locale)}.json`);
      if (!response.ok) throw new Error("locale_load_failed");
      dictionary = await response.json();
    }
    state.locale = locale;
    state.dictionary = dictionary;
    localStorage.setItem("formOfUsLocale", locale);
    document.documentElement.lang = locale;
    document.querySelectorAll("[data-i18n]").forEach((node) => {
      const value = getNested(dictionary, node.dataset.i18n);
      if (typeof value === "string") node.textContent = value;
    });
    const searchInput = document.querySelector("[data-search-input]");
    if (searchInput) searchInput.placeholder = copy("search.input_placeholder", searchInput.placeholder);
    renderProducts();
    document.dispatchEvent(new CustomEvent("formofus:locale-changed", { detail: { locale } }));
  }

  async function loadCatalog() {
    let payload = window.FORM_OF_US_CATALOG;
    if (!payload) {
      const response = await fetch("/catalog/products.json");
      if (!response.ok) throw new Error("catalog_load_failed");
      payload = await response.json();
    }
    state.catalog = payload.products || [];
    renderProducts();
  }

  function setupNavigation() {
    const toggle = document.querySelector("[data-menu-toggle]");
    const nav = document.querySelector("#primary-nav");
    toggle?.addEventListener("click", () => {
      const open = nav.classList.toggle("open");
      toggle.setAttribute("aria-expanded", String(open));
    });
    document.addEventListener("click", (event) => {
      document.querySelectorAll(".nav-menu[open]").forEach((menu) => {
        if (!menu.contains(event.target)) menu.removeAttribute("open");
      });
    });
  }

  function setupCarousel() {
    const carousel = document.querySelector("[data-product-carousel]");
    const amount = () => Math.max(280, carousel.clientWidth * .78);
    document.querySelector("[data-carousel-prev]")?.addEventListener("click", () => {
      carousel.scrollBy({ left: -amount(), behavior: "smooth" });
    });
    document.querySelector("[data-carousel-next]")?.addEventListener("click", () => {
      carousel.scrollBy({ left: amount(), behavior: "smooth" });
    });
  }

  function setupHeroCarousel() {
    const slides = [...document.querySelectorAll("[data-hero-slide]")];
    const dots = [...document.querySelectorAll("[data-hero-dot]")];
    if (!slides.length) return;
    let index = 0;
    let timer;
    const show = (next) => {
      index = (next + slides.length) % slides.length;
      slides.forEach((slide, position) => slide.classList.toggle("active", position === index));
      dots.forEach((dot, position) => {
        dot.classList.toggle("active", position === index);
        dot.setAttribute("aria-current", position === index ? "true" : "false");
      });
    };
    const restart = () => {
      clearInterval(timer);
      timer = setInterval(() => show(index + 1), 6500);
    };
    dots.forEach((dot, position) => dot.addEventListener("click", () => { show(position); restart(); }));
    document.querySelector("[data-hero-prev]")?.addEventListener("click", () => { show(index - 1); restart(); });
    document.querySelector("[data-hero-next]")?.addEventListener("click", () => { show(index + 1); restart(); });
    show(0);
    restart();
  }

  function setupFilters() {
    document.querySelector("[data-catalog-filter]")?.addEventListener("change", (event) => {
      const category = event.target.value;
      document.querySelectorAll("[data-catalog-grid] .product-card").forEach((card) => {
        card.hidden = category !== "all" && card.dataset.category !== category;
      });
    });
  }

  function setupSearch() {
    const dialog = document.querySelector("[data-search-dialog]");
    const input = document.querySelector("[data-search-input]");
    const results = document.querySelector("[data-search-results]");
    document.querySelector("[data-search-open]")?.addEventListener("click", () => dialog.showModal());
    input?.addEventListener("input", () => {
      const query = input.value.trim().toLowerCase();
      if (!query) {
        results.innerHTML = `<p>${escapeHtml(copy("search.popular", "Popular: pet figures · couples · keychains · wedding"))}</p>`;
        return;
      }
      const matches = state.catalog.filter((product) => {
        const title = product.title[state.locale] || product.title["en-US"];
        return `${title} ${product.category}`.toLowerCase().includes(query);
      }).slice(0, 7);
      results.innerHTML = matches.length
        ? matches.map((product) => {
          const title = product.title[state.locale] || product.title["en-US"];
          return `<a href="/products/${encodeURIComponent(product.handle)}">${escapeHtml(title)} · ${money(product.price)}</a>`;
        }).join("")
        : `<p>${escapeHtml(copy("search.no_results", "No matching products yet."))}</p>`;
    });
  }

  function setupInteractions() {
    document.querySelector("[data-locale-select]")?.addEventListener("change", (event) => {
      applyLocale(event.target.value).catch(() => {});
    });
    document.addEventListener("click", (event) => {
      const productButton = event.target.closest("[data-product-handle]");
      if (productButton) window.location.href = `/products/${encodeURIComponent(productButton.dataset.productHandle)}`;
    });
    const backToTop = document.querySelector("[data-back-to-top]");
    window.addEventListener("scroll", () => backToTop?.classList.toggle("visible", window.scrollY > 800), { passive: true });
    backToTop?.addEventListener("click", () => window.scrollTo({ top: 0, behavior: "smooth" }));
  }

  async function initialize() {
    setupNavigation();
    setupCarousel();
    setupHeroCarousel();
    setupFilters();
    setupSearch();
    setupInteractions();
    const localeSelect = document.querySelector("[data-locale-select]");
    if (localeSelect) localeSelect.value = state.locale;
    await Promise.allSettled([loadCatalog(), applyLocale(state.locale)]);
  }

  initialize();
})();
